import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, Typography, Grid, TextField,
  MenuItem, Select, InputLabel, FormControl, CircularProgress, Alert,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import apiService, { authFetch } from '../../services/apiServices';
import { activeWorkers } from '../../utils/workerStatus';

/**
 * WorkerProjectAssignmentForm
 *
 * Props:
 *   - defaultWorkerId  (optional) — pre-select a worker
 *   - hideWorkerSelect (optional) — hide worker dropdown (when used inside EditWorker)
 *   - onSaved          (optional) — callback after successful save
 */
export default function WorkerProjectAssignmentForm({ defaultWorkerId, hideWorkerSelect, onSaved }) {
  const [workers, setWorkers]   = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm]         = useState({
    worker_id:     defaultWorkerId || '',
    project_id:    '',
    start_date:    '',
    end_date:      '',
    hours_per_day: 8,
  });
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  const loadData = useCallback(() => {
    Promise.all([
      apiService().get('workers').catch(() => ({ success: false, data: [] })),
      apiService().get('projects').catch(() => ({ success: false, data: [] })),
    ]).then(([wR, pR]) => {
      setWorkers(activeWorkers(wR.data));
      setProjects(pR.data || []);
    });
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (defaultWorkerId) setForm(f => ({ ...f, worker_id: defaultWorkerId }));
  }, [defaultWorkerId]);

  const handleChange = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    if (!form.worker_id)  return setError('Zgjidhni punëtorin');
    if (!form.project_id) return setError('Zgjidhni projektin');
    if (!form.start_date) return setError('Vendosni datën e fillimit');
    const hpd = +form.hours_per_day || 0;
    if (hpd <= 0 || hpd > 8) return setError('Orët/ditë duhet të jenë midis 0.5 dhe 8');

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const resp = await authFetch(
        `/api/workers/${form.worker_id}/project-assignments`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            project_id:    Number(form.project_id),
            start_date:    form.start_date,
            end_date:      form.end_date || null,
            hours_per_day: +form.hours_per_day || 8,
          }),
        }
      );
      const data = await resp.json();
      if (!resp.ok || !data.success) {
        setError(data.message || data.error || 'Gabim gjatë ruajtjes');
      } else {
        setSuccess('Caktimi u ruajt me sukses!');
        setForm(f => ({ ...f, project_id: '', start_date: '', end_date: '', hours_per_day: 8 }));
        if (onSaved) onSaved(data.data);
      }
    } catch (e) {
      setError('Gabim i papritur: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Cakto punëtorin në projekt
        </Typography>

        {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Grid container spacing={2}>
          {!hideWorkerSelect && (
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Punëtori</InputLabel>
                <Select
                  value={form.worker_id}
                  label="Punëtori"
                  onChange={handleChange('worker_id')}
                >
                  <MenuItem value=""><em>— Zgjidhni —</em></MenuItem>
                  {workers.map(w => (
                    <MenuItem key={w.Id || w.id} value={w.Id || w.id}>
                      {w.FullName || w.fullName || `${w.FirstName || w.first_name || ''} ${w.LastName || w.last_name || ''}`.trim()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Projekti</InputLabel>
              <Select
                value={form.project_id}
                label="Projekti"
                onChange={handleChange('project_id')}
              >
                <MenuItem value=""><em>— Zgjidhni —</em></MenuItem>
                {projects.map(p => (
                  <MenuItem key={p.Id || p.id} value={p.Id || p.id}>
                    {p.Name || p.name || `#${p.Id || p.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Orë/ditë"
              InputLabelProps={{ shrink: true }}
              value={form.hours_per_day}
              onChange={handleChange('hours_per_day')}
              inputProps={{ min: 0.5, max: 8, step: 0.5 }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Data e fillimit"
              InputLabelProps={{ shrink: true }}
              value={form.start_date}
              onChange={handleChange('start_date')}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Data e mbarimit (opsionale)"
              InputLabelProps={{ shrink: true }}
              value={form.end_date}
              onChange={handleChange('end_date')}
              inputProps={{ min: form.start_date || undefined }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={hideWorkerSelect ? 3 : 1}>
            <Box sx={{ display: 'flex', height: '100%', alignItems: 'center' }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                Ruaj
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Punëtori mund të caktohet në disa projekte njëkohësisht. Totali i orëve/ditë nëpër të gjitha caktimet aktive nuk mund të kalojë 8h.
        </Typography>
      </CardContent>
    </Card>
  );
}
